import { z } from 'zod';
// Imports only `zod`: the CLI loads this module from source on the ops host,
// so any other import widens what that host has to resolve.
export const RoutineWeekdaySchema = z.enum(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']);
const isUnique = (values) => new Set(values).size === values.length;
const ClockSchema = z.string().regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/, 'must be HH:MM, 24-hour');
const ClockListSchema = z.array(ClockSchema).min(1).refine(isUnique, 'times must be unique');
const WeekdayListSchema = z.array(RoutineWeekdaySchema).min(1).refine(isUnique, 'weekdays must be unique');
// Days 29 to 31 cannot be listed, so every listed day exists in every month.
const MonthdayListSchema = z.array(z.number().int().min(1).max(28)).min(1).refine((days) => days.every((day, index) => index === 0 || day > days[index - 1]), 'days of the month must be strictly ascending');
const DAY_FILTERS_EXCLUSIVE = { message: 'dayOfMonth and days are mutually exclusive', path: ['dayOfMonth'] };
const hasOneDayFilter = (scope) => scope.days === undefined || scope.dayOfMonth === undefined;
const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD').refine((value) => new Date(`${value}T00:00:00Z`).toISOString().startsWith(value), 'must be a real date');
/** One set of fire times sharing a weekday or day-of-month filter (never both); neither means daily. */
export const RoutineScheduleGroupSchema = z.object({
    at: z.union([ClockSchema, ClockListSchema]),
    days: WeekdayListSchema.optional(),
    dayOfMonth: MonthdayListSchema.optional(),
}).refine(hasOneDayFilter, DAY_FILTERS_EXCLUSIVE);
const WslJobSchema = z.object({
    runner: z.literal('wsl'),
    enabled: z.boolean(),
    procedure: z.string().min(1),
    at: ClockSchema,
    prsPausedUntil: IsoDateSchema.optional(),
    pauseNote: z.array(z.string()).optional(),
});
// A positive integer dividing 60, so the interval lands on the same minutes every hour.
const IntervalMinutesSchema = z.number().int().min(1).refine((minutes) => 60 % minutes === 0, {
    message: 'must be a whole number of minutes that divides 60',
});
const SystemdJobSchema = z.object({
    runner: z.literal('systemd'),
    enabled: z.boolean(),
    procedure: z.string().min(1),
    host: z.string().min(1),
    at: z.union([ClockSchema, ClockListSchema, z.array(RoutineScheduleGroupSchema).min(1)]).optional(),
    everyMinutes: IntervalMinutesSchema.optional(),
    days: WeekdayListSchema.optional(),
    dayOfMonth: MonthdayListSchema.optional(),
}).refine(hasOneDayFilter, DAY_FILTERS_EXCLUSIVE).refine((job) => (job.at === undefined) !== (job.everyMinutes === undefined), { message: 'exactly one of at and everyMinutes is required', path: ['everyMinutes'] }).refine((job) => job.everyMinutes === undefined || (job.days === undefined && job.dayOfMonth === undefined), { message: 'everyMinutes cannot be combined with days or dayOfMonth', path: ['everyMinutes'] }).refine((job) => (job.days === undefined && job.dayOfMonth === undefined) ||
    !(Array.isArray(job.at) && job.at.some((item) => typeof item === 'object')), { message: 'days and dayOfMonth go in each group when at is a list of groups', path: ['at'] });
const ExternalJobSchema = z.object({
    runner: z.literal('external'),
    enabled: z.boolean(),
    procedure: z.string().min(1),
    provider: z.string().min(1),
    registration: z.string().min(1).optional(),
    schedule: z.string().min(1).optional(),
});
// An entry with no `runner` is a legacy WSL job. `runner` must be absent, not merely
// unrecognised, or a mistyped runner would fall through this branch and parse.
const LegacyWslJobSchema = z.object({
    runner: z.undefined().optional(),
    enabled: z.boolean(),
    at: ClockSchema,
    prsPausedUntil: IsoDateSchema.nullable().optional(),
    pauseNote: z.array(z.string()).optional(),
});
export const RoutineEntrySchema = z.union([WslJobSchema, SystemdJobSchema, ExternalJobSchema, LegacyWslJobSchema]);
export const RoutinesManifestSchema = z.object({
    $comment: z.string().optional(),
    routines: z.object({}).catchall(RoutineEntrySchema),
});
const isGrouped = (at) => Array.isArray(at) && at.every((item) => typeof item === 'object');
/** The entry's schedule as groups; the single-time and time-list forms are one group. */
export function scheduleGroups(entry) {
    return isGrouped(entry.at) ? entry.at : [{ at: entry.at, days: entry.days, dayOfMonth: entry.dayOfMonth }];
}
/** The schedule of a `wsl`, legacy or `systemd` entry: calendar groups, or a fixed interval. */
export function scheduleOf(entry) {
    if (entry.everyMinutes !== undefined)
        return { kind: 'interval', minutes: entry.everyMinutes };
    if (entry.at === undefined)
        throw new Error('A schedule needs at or everyMinutes');
    return { kind: 'calendar', groups: scheduleGroups({ ...entry, at: entry.at }) };
}
